import { Entity } from '../../entities/entity';
import { JobStartType } from '../../entities/enums';
import { JobViewType } from '../../entities/job/job.view.type';
import { Location } from '../../entities/shared/location';
import { WorkOrderStatus } from '../../entities/job/workorder.status';
import { AdvancedJobEntity } from '../../entities/entity.advanced.job';
import { Appointment } from '../../entities/shared/appointment';
import { PrimaryTechnician } from '../../entities/job/workorder';
import { Label } from '../../entities/shared/label';
import { StageCompletionBlocker } from '../../entities/tasks/stage.completion.gate';
import { ClaimSubmissionRefusal } from '../../entities/claims/claim.submission.gate';


export enum JobProcessingType {
    NavigateToSite = 'NavigateToSite',
    ArriveAtSite = 'ArriveAtSite',
    Start = 'Start',
    CheckOut = 'CheckOut',
    Hold = 'Hold',
    Complete = 'Complete',
    Finalise = 'Finalise',
    Invoice = 'Invoice',
    Paid = 'Paid',
    Close = 'Close',
    Assign = 'Assign',
    UnAssign = 'UnAssign',
    Dispatch = 'Dispatch',
    New = 'New',
    Reissue = 'Reissue',
    WorkOrderUpdate = 'WorkOrderUpdate',
    JobUpdate = 'JobUpdate',
    ResumeNew = 'ResumeNew',
    ResumeAssigned = 'ResumeAssigned',
    WorkOrderDeleted = 'WorkOrderDeleted',
    WorkOrderAddCode = 'WorkOrderAddCode',
    WorkOrderUpdatedCode = 'WorkOrderUpdatedCode',
    WorkOrderDeletedCode = 'WorkOrderDeletedCode',
    AnswerComplianceQuestions = 'WorkOrderAnswersComplianceQuestion',
    SubcontractorDispatch = 'SubcontractorDispatch',
    SubcontractorUnAssign = 'SubcontractorUnAssign',
    SubcontractorNew = 'SubcontractorNew',
    MarkCompliance = 'MarkCompliance',
    AppointmentUpdated = 'AppointmentUpdated'
}

export interface JobProcessingRequest {
    jobId: string;
    type: JobProcessingType;
    note?: string;
    date?: Date;
    user?: Entity;
    status?: string;
    signature?: string; // for complete
    startType?: JobStartType; // for start
    selectedViewMode?: JobViewType;
    isResume?: boolean;
    isSubcontractor?: boolean;
    timestamp: string;
    cancelAppointments?: boolean;
    location?: Location;
    systemStatusName?: string;
}

export interface JobProcessingDataRequest {
    jobId: string;
    systemStatusName: string;
    note?: string;
    date?: Date;
    updatedAt?: string;
    jobStatusCanBeChanged?: boolean;
}

export interface WorkOrderProcessingDataRequest {
    jobId: string;
    workOrderId: string;
    systemStatusName: string;
    note?: string;
    date?: Date;
    updatedAt?: string;
    location?: Location;
    technician?: Entity;
    cancelAppointments?: boolean;
    //TODO: remove the allocationId if no longer use it.
    allocationId?: string;
    allocationIds?: string[];
    mobile?: boolean;
    originalStatusName?: string;
    reverse?: boolean;
    reverseNote?: string;
    completedSignatureName?: string;
    completedSignature?: string;
    affectedWorkOrder?: boolean;
    fromSupervisor?: boolean;
    skipMandatoryTasks?: boolean;
    skipReason?: string;
    otherSkipReason?: string;
    validationOnly?: boolean;
    deleteValidation?: boolean;
    /** Web App status editor only: a permitted user authorised completing the stage with unresolved tasks. Ignored on every other route. */
    overrideStageCompletionGate?: boolean;
    /** Web App status editor only: a permitted user authorised approving the claim with unresolved tasks. Ignored on every other route. */
    overrideClaimSubmissionGate?: boolean;
}

export interface ClaimWorkOrderProcessingDataRequest extends WorkOrderProcessingDataRequest {
    claimToken?: string;
}

export interface WorkOrderProcessFields {
    status?: WorkOrderStatus;
    technician?: AdvancedJobEntity;
    resources?: PrimaryTechnician[];
    completedDate?: Date;
    completedNote?: string;
    finalisedDate?: Date;
    finalisedNote?: string;
    notifyTechnician?: boolean;
    subcontractor?: AdvancedJobEntity;
    version?: number;
    previousStatus?: AdvancedJobEntity;
    firstInProgressAt?: Date;
    appointments?: Appointment[];
    additionalNote?: string;
    contractorResources?: PrimaryTechnician[];
    completedSignatureName?: string;
    completedSignature?: string;
    completedBy?: Entity;
    finalisedBy?: Entity;
    claimToken?: string;
    systemLabels?: Label[]
}

export interface WorkOrderProcessUpdateRequest {
    workOrderId: string;
    updatedAt: string;
    fields: WorkOrderProcessFields;
}


export enum WorkOrderProcessingType {
    CheckedOut = 'Checked Out',
    Cancelled = 'Cancelled',
    Billed = 'Billed',
    Closed = 'Closed',
    Assigned = 'Assigned',
    Completed = 'Completed',
    Onsite = 'Onsite',
    Paid = 'Paid',
    Dispatched = 'Dispatched',
    OnHold = 'On Hold',
    New = 'New',
    Enroute = 'Enroute',
    InProgress = 'InProgress',
    Finalised = 'Finalised',
    UnAssigned = 'UnAssigned',
    Reissue = 'Reissue',
    ResumeNew = 'ResumeNew',
    ResumeAssigned = 'ResumeAssigned',
    SubcontractorNew = 'SubcontractorNew',
    SubcontractorResumeAssigned = 'SubcontractorResumeAssigned',
    SubcontractorResumeNew = 'SubcontractorResumeNew',
    SubcontractorDispatch = 'SubcontractorDispatch',
    SubcontractorUnAssign = 'SubcontractorUnAssign',
    JobResumeNewOrAssigned = 'JobResumeNewOrAssigned',
    Updated = 'Updated',
    RunsheetDispatched = 'RunsheetDispatched',
    RunsheetUnAssigned = 'RunsheetUnAssigned',
    AppointmentDispatched = 'AppointmentDispatched',
    RunsheetDeleted = 'RunsheetDeleted',
    WorkOrderDeleted = 'WorkOrderDeleted',
    Reverse = 'Reverse',
}

export interface WorkOrderProcessingValidationRequest {
    jobId: string;
    workOrderId?: string;
    systemStatusName: string;
    action?: 'delete' | 'update';
}

export interface WorkOrderProcessingValidationResponse {
    valid: boolean;
    messages?: string[];
    /** Tasks holding the stage (or, for a job, any of its stages) open; the dialog lists them instead of the change summary. */
    blockingTasks?: StageCompletionBlocker[];
    /** Everything holding the stage's claim when a claim action is picked; the dialog lists it and offers the override only when open tasks alone hold it. */
    claimRefusal?: ClaimSubmissionRefusal;
}